// src/scenes/balance/ViewBalanceSheet.jsx
import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
   Select,
    MenuItem,
    FormControl,
    InputLabel,
  Typography,
  Divider,
  TextField,
  useTheme,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { tokens } from "../../theme";
import BalanceSheetForm from "./editBalance";

/* ---------- helpers ---------- */
const fmtDate = (iso) => {
  if (!iso) return "-";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};

const fmtNum = (num) => {
  if (typeof num !== "number" || isNaN(num) || num === 0) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewBalanceSheet() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const [detailRow, setDetailRow] = React.useState(null);
  const [editId, setEditId] = React.useState(null);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  const [customers, setCustomers] = React.useState([]);
  const [selectedCustomer, setSelectedCustomer] = React.useState("");
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");

  /* ---------- helpers ---------- */
  const showToast = (msg, sev = "success") =>
    setToast({ open: true, msg, sev });

  /* ---------- fetch ---------- */
  const loadBalanceSheet = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/balance-sheet", {
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      _t: Date.now(),
    });
    setLoading(false);

    if (error) {
      setToast({ open: true, msg: "Failed to load balance sheet", sev: "error" });
      return;
    }

    const allCustomers = data.data.map((c) => ({
      customerId: c.customerId,
      customerName: c.customerName,
      totals: c.totals,
      rows: c.rows,
    }));
    setCustomers(allCustomers);

    if (!selectedCustomer && allCustomers.length)
      setSelectedCustomer(allCustomers[0].customerId);

    const selected =
      allCustomers.find((c) => c.customerId === selectedCustomer) ||
      allCustomers[0];
    if (selected) {
      const formattedRows = selected.rows.map(r => ({
        ...r,
        bankDeposit: fmtNum(parseFloat(r.bankDeposit)),
        withHold: fmtNum(parseFloat(r.withHold)),
        invoiceAmount: fmtNum(parseFloat(r.invoiceAmount)),
        adjustment: fmtNum(parseFloat(r.adjustment)),
        endingBalance: fmtNum(parseFloat(r.endingBalance)),
        isSummary: false,
      }));

   

    formattedRows.push({
      id: "summary-row",
      agentName: "TOTAL",
      Customer: { name: "" },
      Item: { name: "" },
      date: "",
      bankDeposit: fmtNum(selected.totals.totalBankDeposit),
      withHold: fmtNum(selected.totals.totalWithHold),
      invoiceAmount: fmtNum(selected.totals.totalInvoiceAmount),
      adjustment: fmtNum(selected.totals.adjustment),
      endingBalance: fmtNum(selected.totals.endingBalance),
      isSummary: true,
    });

     setRows(formattedRows);
    }
  }, [selectedCustomer, startDate, endDate]);

  React.useEffect(() => {
    loadBalanceSheet();
  }, [loadBalanceSheet]);

  /* ---------- delete ---------- */
  const handleDelete = async () => {
    if (!deleteRow?.id) return;

    setDeleting(true);
    const { error } = await deleteJSON(`/api/balance-sheet/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      showToast("Delete failed", "error");
      return;
    }

    setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
    showToast("Deleted successfully", "success");
    setDeleteRow(null);
    loadBalanceSheet();
  };

  /* ---------- columns ---------- */
  const columns = [
        {
    field: "no",
    headerName: "No.",
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    valueGetter: (params) => {
      // index among visible rows on the current page
      const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(params.id);
      // page & pageSize for absolute numbering across pages
      const pm = params.api?.state?.pagination?.paginationModel || { page: 0, pageSize: rows.length || 0 };
      const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
      return pageOffset + visibleIndex + 1;
    }
  },  
    { field: "agentName", headerName: "Agent", width: 160 },
    {
      field: "Customer",
      headerName: "Customer",
      width: 160,
      valueGetter: (p) => p.row.Customer?.name || "-",
    },
    {
      field: "Item",
      headerName: "Item",
      width: 140,
      valueGetter: (p) => p.row.Item?.name || "-",
    },
    {
      field: "date",
      headerName: "Date",
      width: 120,
      valueGetter: (p) => fmtDate(p.row.date),
    },
    { field: "bankDeposit", headerName: "Bank Deposit", width: 130 },
    { field: "withHold", headerName: "WithHold", width: 120 },
    { field: "invoiceAmount", headerName: "Invoice Amount", width: 140 },
    { field: "adjustment", headerName: "Adjustment", width: 120 },
    { field: "endingBalance", headerName: "Ending Balance", width: 140 },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        if (params.row.isSummary) return null;
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {/* <Tooltip title="Edit">
              <IconButton size="small" onClick={() => setEditId(row.id)}>
                <EditIcon />
              </IconButton>
            </Tooltip> */}

            <Tooltip title="Delete">
              <Box sx={{ position: "relative" }}>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setDeleteRow(row)}
                  disabled={deleting && deleteRow?.id === row.id}
                >
                  <DeleteOutlineIcon />
                </IconButton>
                {deleting && deleteRow?.id === row.id && (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  /* ---------- grid style ---------- */
  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .summary-row .MuiDataGrid-cell": { fontWeight: "bold" },
  };

  return (
    <Box m="20px">
      <Header
        title="BALANCE SHEET"
        subtitle="View, edit, and manage balance sheet entries"
      />

      {/* Filters */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap">
          <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    value={selectedCustomer}
                    label="Customer"
                    onChange={(e) => setSelectedCustomer(e.target.value)}
                  >
                    {customers.map((c) => (
                      <MenuItem key={c.customerId} value={c.customerId}>
                        {c.customerName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
        <TextField
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={loadBalanceSheet}>
          Filter
        </Button>
      </Box>

      {/* Table */}
      <Box m="20px 0 0 0">
        <SimpleTable
          rows={rows}
          columns={columns}
          loading={loading}
          height="70vh"
          getRowId={(r) => r.id}
          sx={gridSx}
          getRowClassName={(params) =>
            params.row.isSummary ? "summary-row" : ""
          }
        />
      </Box>

      {/* Edit Dialog */}
      <BalanceSheetForm
        id={editId}
        open={!!editId}
        onClose={() => setEditId(null)}
        onUpdated={() =>
          showToast("Balance sheet updated successfully", "success")
        }
      />

      {/* Delete Dialog */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Entry</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete this entry?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert severity={toast.sev} variant="filled" sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
